import { useState } from 'react'
import { Download } from 'lucide-react'
import { Button, Skeleton } from '../../../modules/core/index.js'
import { EndpointHint } from '../../../modules/core/index.js'
import { FeatureGate } from '../../../modules/add-ons/index.js'
import { useFeature } from '../../../modules/add-ons/index.js'
import { FORM_INPUT_CLASS } from '../../../modules/core/index.js'
import type { RosterContext } from '../../../modules/core/index.js'
import { useExportForHr, useFacilityExports } from '../hooks.js'
import { FEATURE_KEY, FEATURE_NAME } from '../labels.js'
import { ReceiptList } from './ReceiptList.js'
import { LoadFailure } from '../../../modules/core/index.js'

/** The years on offer: this one (closed months so far) and the two before. */
function yearChoices(): string[] {
  const year = new Date().getFullYear()
  return [String(year), String(year - 1), String(year - 2)]
}

function Card({ facilityId }: { facilityId: string }) {
  const [span, setSpan] = useState(yearChoices()[0] ?? '')
  const exportForHr = useExportForHr(facilityId)
  const receipts = useFacilityExports(facilityId)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[11px] font-bold text-ink-mid">
          Year
          <select className={`${FORM_INPUT_CLASS} w-auto`} value={span} onChange={(event) => setSpan(event.target.value)}>
            {yearChoices().map((year, index) => (
              <option key={year} value={year}>
                {year}
                {index === 0 ? ' (closed months so far)' : ''}
              </option>
            ))}
          </select>
        </label>
        <Button icon={Download} onClick={() => exportForHr.mutate(span)} disabled={exportForHr.isPending}>
          {exportForHr.isPending ? 'Exporting…' : 'Export for HR'}
        </Button>
        <span className="text-[11px] text-ink-mid">One spreadsheet row per worker on the roster, with each summary's fingerprint. Every export is kept as a receipt.</span>
      </div>
      {exportForHr.isError && (
        <p role="alert" className="text-[11px] text-coral">
          {exportForHr.error instanceof Error ? exportForHr.error.message : 'The export failed. Try again.'}
        </p>
      )}
      {exportForHr.isSuccess && (
        <p role="status" className="text-[11px] text-ink">
          <span className="font-bold">Saved {exportForHr.data.fileName}</span>: {exportForHr.data.rows} {exportForHr.data.rows === 1 ? 'worker' : 'workers'}
          {exportForHr.data.failed.length > 0 && <span className="text-coral"> · {exportForHr.data.failed.length} could not be summarised ({exportForHr.data.failed.join(', ')})</span>}
        </p>
      )}
      {receipts.isPending ? (
        <Skeleton label="Loading receipts" />
      ) : receipts.isError ? (
        <LoadFailure size="xs" message="Could not load the export receipts." onRetry={() => void receipts.refetch()} retrying={receipts.isFetching} />
      ) : (
        <ReceiptList receipts={receipts.data} empty="Nothing has been exported for this facility yet." />
      )}
    </div>
  )
}

/** The Reporting tab's section: Export for HR with its receipts. Nothing for a client without the add-on; the switched-off line or the card otherwise. */
export function HrExportCard({ facilityId, role, addOnsHref }: RosterContext) {
  const standing = useFeature(facilityId, FEATURE_KEY)
  if (standing.isLoading || !standing.licensed) {
    return null
  }
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2 border-b border-marine/15 pb-2 text-[10px] font-bold uppercase tracking-wide text-marine">
        Performance summaries for HR
        <EndpointHint>GET /facilities/{'{facilityId}'}/performance-summaries/export</EndpointHint>
      </div>
      <FeatureGate feature={FEATURE_KEY} name={FEATURE_NAME} facilityId={facilityId} role={role} addOnsHref={addOnsHref} compact>
        <Card facilityId={facilityId} />
      </FeatureGate>
    </div>
  )
}
